import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, CheckCircle, XCircle, Activity } from 'lucide-react';
import ErrorLog from './ErrorLog';
import SystemMetrics from './SystemMetrics';

const SystemHealth = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchHealth();
    // Refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/system/health`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setHealth(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching system health:', err);
      setError(err.response?.data?.message || 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
          <span className="text-sm text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100';
      case 'down':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5" />;
      case 'down':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
          <p className="mt-1 text-sm text-gray-600">
            Monitor errors, performance, and system status
          </p>
        </div>
        <button
          onClick={fetchHealth}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      <div className={`rounded-lg border-2 p-6 ${
        health?.status === 'healthy'
          ? 'border-green-200 bg-green-50'
          : health?.status === 'degraded'
          ? 'border-yellow-200 bg-yellow-50'
          : 'border-red-200 bg-red-50'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`rounded-full p-3 ${getStatusColor(health?.status)}`}>
              {getStatusIcon(health?.status)}
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                System Status: {health?.status?.toUpperCase() || 'UNKNOWN'}
              </h3>
              <p className="text-sm text-gray-600">
                Last checked: {health?.lastChecked ? new Date(health.lastChecked).toLocaleString() : 'Never'}
              </p>
            </div>
          </div>
          {health?.uptime && (
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {health.uptime.toFixed(2)}%
              </div>
              <div className="text-sm text-gray-600">Uptime (24h)</div>
            </div>
          )}
        </div>
      </div>

      {/* Service Status Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {health?.services && Object.entries(health.services).map(([service, status]) => (
          <div key={service} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    {service.replace('_', ' ')}
                  </h4>
                  <div className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                    {status}
                  </div>
                </div>
                <div className={getStatusColor(status)}>
                  {getStatusIcon(status)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* System Metrics */}
      <SystemMetrics metrics={health?.metrics} />

      {/* Error Log */}
      <ErrorLog />
    </div>
  );
};

export default SystemHealth;
